import React from 'react';
import {
  Box, Stack, Typography, Chip, Button, Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import StandardDialog, { StandardDialogActions } from './StandardDialog';
import { useAppSelector } from '../../app/hooks';
import { BestPractice } from '../../interfaces/bestPractice.interface';
import { useDispatch } from 'react-redux';
import { setBestPractice, setQuestion } from '../../app/store/questionnaireAnswerSlice';
import { Assistant } from '@mui/icons-material';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  isNewCode?: (code: string) => boolean;
};

const Dot = ({ active }: { active: boolean }) => (
  <Box
    component="span"
    sx={{
      color: active ? 'primary.main' : 'text.disabled',
      display: 'inline-flex',
      alignItems: 'center',
      ml: 0.25,
    }}
  >
    <FiberManualRecordIcon sx={{ fontSize: 9 }} />
  </Box>
);

export default function AutoAnsweredByCertModal({
  isOpen,
  onClose,
  isNewCode = () => true,
}: Props) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { answeredByCertModalData } = useAppSelector((state) => state.uiSlice);

  if (!answeredByCertModalData) return null;

  const pillBase = {
    fontSize: '0.95rem',
    px: 1.25,
    py: 0.25,
    borderRadius: '999px',
    fontWeight: 400,
    '& .MuiChip-label': { fontWeight: 400, color: 'inherit' },
  } as const;

  const pillNew = {
    ...pillBase,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.text.primary,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
  } as const;

  const pillOld = {
    ...pillBase,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    border: `1px solid ${alpha(theme.palette.text.primary, 0.18)}`,
  } as const;

  const handleBPClick = (bp:BestPractice, code: string) => {
    const hasQuestions = bp.questions.findIndex(q => typeof q === 'number');

    if (hasQuestions !== -1) return;

    dispatch(setBestPractice({ ...bp, domain: typeof bp.domain === 'number' ? bp.domain : bp.domain?.id, topic: typeof bp.topic === 'number' ? bp.topic : bp.topic?.id }));
    dispatch(setQuestion(bp.questions.find(q => q.shortCode === code)!));
    onClose();
  };

  return (
    <StandardDialog
      title={
        <Box sx={{ display: 'flex,', alignItems: 'center', gap: 1 }}>
          <Assistant sx={{ opacity: 0.9 }} />
          <Typography variant="h5" component="span">
            Auto-Answered from Global Pass & ISO Documents provided by Member
          </Typography>
        </Box>
      }
      handleClose={onClose}
      isOpen={isOpen}
    >
      <Stack spacing={2.5}>
      <Box
          sx={{
            px: 1,
            pt: 0.5,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            color={theme.palette.customThemeColors?.darkGrey}
            sx={{ fontWeight: 700, lineHeight: 1.4, mr: 1 }}
          >
            {answeredByCertModalData.titles.length > 1 ? 'File:' : 'File:'}
          </Typography>

          <Stack
            direction="row"
            useFlexGap
            flexWrap="wrap"
            gap={1}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {answeredByCertModalData.titles.map((t) => (
              <Tooltip key={t} title={t}>
                <Chip
                  label={
                    <Box
                      sx={{
                        maxWidth: 260,
                        display: 'inline-block',
                        overflow: 'hidden',
                        lineHeight: 1,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t}
                    </Box>
                  }
                  color="primary"
                  variant="outlined"
                  size="medium"
                  sx={{
                    fontSize: '0.95rem',
                    px: 1.25,
                    py: 0.25,
                    display: 'flex',
                    borderRadius: '999px',
                    alignSelf : 'center',
                    justifyContent: 'center',
                    alignItems: 'center',
                    lineHeight: 1,
                    fontWeight: 500,
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
        </Box>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.6, fontSize: '1.05rem' }}
        >
          Auto-answered questions for your uploaded file(s) are shown in{' '}
          <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
            blue
          </Box>
          . You may review or edit them any time before submitting your questionnaire.
        </Typography>
        <Stack spacing={1.25} sx={{ pl: 1 }}>
          {Boolean(answeredByCertModalData.bpCodes.length === 0 && answeredByCertModalData.arCodes.length === 0) && (
            <Typography
              sx={{
                fontSize: '1.1rem',
                fontWeight: 400,
                color: theme.palette.customThemeColors?.darkGrey,
                textAlign: 'center',
                fontStyle: 'italic',
                pt: 3,
                pb: 4,
              }}
            >
              No questions were auto-answered by the provided documents
            </Typography>
          )}

          {Boolean(answeredByCertModalData.bpCodes.length > 0) && (
            <Stack spacing={1}>
              <Typography
                sx={{
                  fontWeight: 500,
                  color: theme.palette.customThemeColors?.darkGrey,
                  fontSize: '1.1rem',
                  mb: 0.25,
                }}
              >
                Best Practices:
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                sx={{ rowGap: 0.5, mt: 0 }}
              >
                {answeredByCertModalData.bpCodes.map(({ code, bestPractice }) => {
                  const isNew = isNewCode(code);
                  return (
                  <Tooltip
                    key={code}
                    placement='top'
                    title={bestPractice.questions.find(q => q.shortCode === code)?.title || ''}
                    enterDelay={400}
                    arrow
                  >
                    <Chip
                      onClick={() => handleBPClick(bestPractice, code)}
                      size="medium"
                      label={code}
                      variant="filled"
                      icon={<Dot active={isNew} />}
                      sx={isNew ? pillNew : pillOld}
                    />
                  </Tooltip>
                  );
                })}
              </Stack>
            </Stack>
          )}

          {Boolean(answeredByCertModalData.arCodes.length > 0) && (
            <Stack spacing={1}>
              <Typography
                sx={{
                  fontWeight: 500,
                  color: theme.palette.customThemeColors?.darkGrey,
                  fontSize: '1.1rem',
                  mb: 0.25,
                  pt: 1,
                }}
              >
                Additional Recommendations:
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                sx={{ rowGap: 0.5, mt: 0 }}
              >
              {answeredByCertModalData.arCodes.map(({ code, bestPractice }) => {
                const isNew = isNewCode(code);
                return (
                  <Tooltip
                    key={code}
                    placement='top'
                    title={bestPractice.questions.find(q => q.shortCode === code)?.title || ''}
                    enterDelay={400}
                    arrow
                  >
                    <Chip
                      onClick={() => handleBPClick(bestPractice, code)}
                      size="medium"
                      label={code}
                      variant="filled"
                      icon={<Dot active={isNew} />}
                      sx={isNew ? pillNew : pillOld}
                    />
                  </Tooltip>
                );
              })}
              </Stack>
            </Stack>
          )}
        </Stack>

        {Boolean(answeredByCertModalData.bpCodes.length > 0 || answeredByCertModalData.arCodes.length > 0) && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ pt: 2, pb: 1, lineHeight: 1.7, fontSize: '1.1rem' }}
          >
            <Box
              component="span"
              sx={{ color: theme.palette.customThemeColors?.darkGrey, fontWeight: 500 }}
            >
              Note:
            </Box>{' '}
            These auto-answered questions will be assessed like all others.
          </Typography>
        )}
      </Stack>
      <StandardDialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ fontSize: '0.95rem', px: 2.5, py: 0.75 }}
        >
          Close
        </Button>
      </StandardDialogActions>
    </StandardDialog>
  );
}
